export type CoverageLockEvent = {
  action?: string;
  reason?: string;
  roundId: number;
  projectIntId: number;
  projectKey?: string;
  personId?: number;
  judgeName?: string;
  projectCode?: string;
  title?: string;
  expiresAt?: string;
  at?: number;
};

export function coverageLockMillis(value?: string): number {
  if (!value) return 0;
  const match = /\/Date\((\d+)\)\//.exec(String(value));
  if (match) return parseInt(match[1], 10);
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function normalizeCoverageLock(raw: any): CoverageLockEvent | null {
  if (!raw) return null;
  const roundId = Number(raw.roundId ?? raw.RoundId);
  const projectIntId = Number(raw.projectIntId ?? raw.ProjectIntId);
  if (!roundId || !projectIntId) return null;
  return {
    action: raw.action || raw.Action || "lock",
    reason: raw.reason || raw.Reason || "",
    roundId,
    projectIntId,
    projectKey: String(raw.projectKey || raw.ProjectKey || "").toLowerCase(),
    personId: Number(raw.personId ?? raw.PersonId) || 0,
    judgeName: raw.judgeName || raw.JudgeName || "",
    projectCode: raw.projectCode || raw.ProjectCode || "",
    title: raw.title || raw.Title || "",
    expiresAt: raw.expiresAt || raw.ExpiresAt || "",
    at: Number(raw.at ?? raw.At) || Date.now(),
  };
}

export function coverageLockKey(event: CoverageLockEvent) {
  return `${event.roundId}:${event.projectIntId}`;
}

export function reduceCoverageLocks(
  current: Record<string, CoverageLockEvent>,
  event: CoverageLockEvent
): Record<string, CoverageLockEvent> {
  const key = coverageLockKey(event);
  const prev = current[key];
  if (prev && (prev.at || 0) > (event.at || 0)) return current;
  const alive = event.action !== "unlock" && coverageLockMillis(event.expiresAt) > Date.now();
  if (!alive) {
    if (!prev) return current;
    const next = { ...current };
    delete next[key];
    return next;
  }
  return { ...current, [key]: event };
}

export function dropExpiredCoverageLocks(current: Record<string, CoverageLockEvent>) {
  const now = Date.now();
  let changed = false;
  const next = { ...current };
  Object.keys(next).forEach((key) => {
    if (coverageLockMillis(next[key].expiresAt) <= now) {
      delete next[key];
      changed = true;
    }
  });
  return changed ? next : current;
}

export function mergeCoverageSnapshot(
  current: Record<string, CoverageLockEvent>,
  rows: any[],
  sentAt: number
) {
  let next = current;
  const seen: Record<string, boolean> = {};
  (rows || []).forEach((raw) => {
    const event = normalizeCoverageLock(raw);
    if (!event) return;
    if (!event.action) event.action = "lock";
    seen[coverageLockKey(event)] = true;
    next = reduceCoverageLocks(next, event);
  });
  const copy = next === current ? { ...current } : { ...next };
  let changed = next !== current;
  Object.keys(copy).forEach((key) => {
    if (!seen[key] && (copy[key].at || 0) <= sentAt) {
      delete copy[key];
      changed = true;
    }
  });
  return changed ? copy : current;
}
